// @ts-nocheck

import { reduce, some, every, fill, flatten, chunk, indexOf } from 'lodash';

import { COST_PER_GRID } from '../constants';
import { GridsCheckTypes, SelectionCheckTypes, CheckIndexes } from '../types';
import type { GridsCheckType } from '../types';

class Selection {
  static getCheckIndex = (checkType: GridsCheckType): number => CheckIndexes[checkType];

  static hasEnoughChecks = (selection: string): boolean => {
    if (selection && selection.length > 0) {
      const checks = getChecksPerLine(selection);
      return reduce(
        checks,
        (acc, cur) => (!acc ? false : some(cur, (value) => value === SelectionCheckTypes.CHECK)),
        true,
      );
    }
    return false;
  };

  static hasEnoughChecksForAllSelections = (selections: Array<string>) => {
    if (selections && selections.length > 0) {
      return !some(selections, (selection) => Selection.hasEnoughChecks(selection) === false);
    }
    return false;
  };

  static isChecked = (selection: string, matchIndex: number, checkType: GridsCheckType): boolean => {
    if (selection) {
      const checks = getChecksPerLine(selection);
      const checkIndex = Selection.getCheckIndex(checkType);
      const line = checks[matchIndex];
      if (line) {
        const check = line[checkIndex];
        return check ? check === SelectionCheckTypes.CHECK || check === SelectionCheckTypes.BASE : false;
      }
      return false;
    }
    return false;
  };

  static isBase = (selection: string, matchIndex: number, checkType: GridsCheckType): boolean => {
    if (selection) {
      const checks = getChecksPerLine(selection);
      const checkIndex = Selection.getCheckIndex(checkType);
      const line = checks[matchIndex];
      if (line) {
        const check = line[checkIndex];
        return check ? check === SelectionCheckTypes.BASE : false;
      }
      return false;
    }
    return false;
  };

  static isWinningLine = (selection: string, resultSelection: string | null, matchIndex: number): boolean => {
    if (resultSelection && selection) {
      const checksSelection = getChecksPerLine(selection);
      const checksResult = getChecksPerLine(resultSelection);
      const lineSelection = checksSelection[matchIndex];
      const lineResult = checksResult[matchIndex];
      const winningIndex = lineResult.indexOf(SelectionCheckTypes.CHECK);
      return (
        lineSelection[winningIndex] === SelectionCheckTypes.CHECK ||
        lineSelection[winningIndex] === SelectionCheckTypes.BASE
      );
    }
    return false;
  };

  static toggleCheck = (selection: string, matchIndex: number, checkType: GridsCheckType): string => {
    // we don't want reverse checks line because we set the grid, we don't read it
    const checks = getChecksPerLine(selection, true);
    const inverseMatchIndex = checks.length - 1 - matchIndex;
    const checkIndex = Selection.getCheckIndex(checkType);
    const check = checks[inverseMatchIndex][checkIndex];
    checks[inverseMatchIndex][checkIndex] =
      check === SelectionCheckTypes.UNCHECK ? SelectionCheckTypes.CHECK : SelectionCheckTypes.UNCHECK;
    return flatten(checks).join('');
  };

  static getCompactSystem = (systemStrGridSelections: Array<string>): string => {
    const padSystemSelections = systemStrGridSelections.map((selection) => Selection.pad(selection));
    const emptySelection = Selection.emptySelection(padSystemSelections[0]);

    const resultsByChecks = emptySelection.split('').reduce((result, cur, checkIndex) => {
      return [...result, padSystemSelections.reduce((acc, str) => [...acc, str.charAt(checkIndex)], '')];
    }, []);

    return resultsByChecks.reduce((acc, result) => {
      if (every(result, (k) => k === SelectionCheckTypes.CHECK)) {
        return acc + SelectionCheckTypes.BASE;
      }
      if (some(result, (k) => k === SelectionCheckTypes.CHECK)) {
        return acc + SelectionCheckTypes.CHECK;
      }
      return acc + SelectionCheckTypes.UNCHECK;
    }, '');
  };

  static createEmpty = (matchesCount: number): string =>
    matchesCount ? fill(Array(matchesCount * Object.keys(GridsCheckTypes).length), 0).join('') : '';

  static emptySelection = (selection: string): string => {
    const checks = getChecksPerLine(selection);
    return Selection.createEmpty(checks.length);
  };

  static isEmpty = (selection: string): boolean => {
    return selection ? !(selection.indexOf('1') !== -1 || selection?.indexOf('2') !== -1) : true;
  };

  // Decompose selection on individuals selections (1 check per line)
  // ex: 110 -> 100, 010
  static getIndividualSelections = (selection: string): Array<string> => {
    const checks = getChecksPerLine(selection);
    let results = [selection];
    checks.forEach((line, lineIndex) => {
      const results2 = [];
      results.forEach((result) => {
        const offset = lineIndex * 3;
        const nextOffset = offset + 3;
        if (line[0] === SelectionCheckTypes.CHECK) {
          results2.push(`${result.substring(0, offset)}100${result.substring(nextOffset)}`);
        }

        if (line[1] === SelectionCheckTypes.CHECK) {
          results2.push(`${result.substring(0, offset)}010${result.substring(nextOffset)}`);
        }

        if (line[2] === SelectionCheckTypes.CHECK) {
          results2.push(`${result.substring(0, offset)}001${result.substring(nextOffset)}`);
        }
      });
      results = results2;
    });
    return results;
  };

  static getCountDoubles = (selection: string): number => {
    const checks = getChecksPerLine(selection);
    return checks.reduce((result, line) => {
      const nbChecks = line.reduce((acc, cur) => (cur === SelectionCheckTypes.CHECK ? acc + 1 : acc), 0);
      return nbChecks === 2 ? result + 1 : result;
    }, 0);
  };

  static getCountTriples = (selection: string): number => {
    const checks = getChecksPerLine(selection);
    return checks.reduce((result, line) => {
      const nbChecks = line.reduce((acc, cur) => (cur === SelectionCheckTypes.CHECK ? acc + 1 : acc), 0);
      return nbChecks === 3 ? result + 1 : result;
    }, 0);
  };

  // ex: 1 checks per line -> 1
  // ex: 1 checks per line + 1 check -> 2
  static getIndividualGridsCount = (selection: string, includeUncheckedLines: boolean = false): number => {
    const checks = getChecksPerLine(selection);
    return checks.reduce((result, line) => {
      let nbChecks = line.reduce((acc, cur) => (cur === SelectionCheckTypes.CHECK ? acc + 1 : acc), 0);
      if (includeUncheckedLines && nbChecks === 0) {
        nbChecks = 1;
      }
      return result * nbChecks;
    }, 1);
  };

  // Get score of individual selection
  static getScore = (selection: string, results: string): number => {
    const checks = selection.split('');
    const resultsChecks = results.split('');
    return checks.reduce((score, check, index) => {
      const resultCheck = resultsChecks[index];
      const isCheck = check === SelectionCheckTypes.CHECK;
      const hasOccured = resultCheck === SelectionCheckTypes.CHECK;
      return isCheck && hasOccured ? score + 1 : score;
    }, 0);
  };

  static inverse = (selection: string): string => {
    const checks = getChecksPerLine(selection);
    return flatten(checks).join('');
  };

  static pad = (selection: string): string => {
    let paddedGridSelection = selection;
    const lengthMod3 = paddedGridSelection.length % 3;
    if (lengthMod3 === 1) {
      paddedGridSelection = `00${paddedGridSelection}`;
    } else if (lengthMod3 === 2) {
      paddedGridSelection = `0${paddedGridSelection}`;
    }
    return paddedGridSelection;
  };

  static getAmount = (selection: string): number => {
    return Selection.getIndividualGridsCount(selection) * COST_PER_GRID;
  };

  static checkRandomly = (selection: string, nbDoubles: number = 0, nbTriples: number = 0): string => {
    const checks = getChecksPerLine(selection);
    const nbLines = checks.length;

    if (nbLines > 0) {
      const doublesIndexes = [];
      const triplesIndexes = [];
      for (let i = 1; i <= nbDoubles; i += 1) {
        for (;;) {
          const index = Math.floor(Math.random() * nbLines);
          if (indexOf(doublesIndexes, index) === -1) {
            doublesIndexes.push(index);
            break;
          }
        }
      }
      for (let i = 1; i <= nbTriples; i += 1) {
        for (;;) {
          const index = Math.floor(Math.random() * nbLines);
          if (indexOf(doublesIndexes, index) === -1 && indexOf(triplesIndexes, index) === -1) {
            triplesIndexes.push(index);
            break;
          }
        }
      }

      return checks.reduce((acc, check, i) => {
        let line = '';
        if (indexOf(triplesIndexes, i) !== -1) {
          line = '111';
        } else if (indexOf(doublesIndexes, i) !== -1) {
          const rand = Math.floor(Math.random() * 3 + 1);
          line += rand !== 1 ? '1' : '0';
          line += rand !== 2 ? '1' : '0';
          line += rand !== 3 ? '1' : '0';
        } else {
          const rand = Math.floor(Math.random() * 3 + 1);
          line += rand === 1 ? '1' : '0';
          line += rand === 2 ? '1' : '0';
          line += rand === 3 ? '1' : '0';
        }
        return acc + line;
      }, '');
    }
    return selection;
  };

  static fillRandomly = (selection: string): string => {
    // If not enough checks, we only fill the empty lines randomly. If enough checks, we replace with completly random
    const randomSelection = Selection.checkRandomly(selection);
    if (Selection.hasEnoughChecks(selection)) {
      return randomSelection;
    }
    const checks = getChecksPerLine(selection, true);
    const randomChecks = getChecksPerLine(randomSelection, true);
    return checks.reduce((acc, cur, index) => {
      if (cur.indexOf('1') !== -1) {
        return acc + cur.join('');
      }
      return acc + randomChecks[index].join('');
    }, '');
  };
}

export const getChecksPerLine = (selection: string, noReverse: boolean = false): Array<Array<string>> => {
  // we reverse because selection matches lines are stored on inverse way of matches array
  const chunks = chunk(Selection.pad(selection).split(''), Object.keys(GridsCheckTypes).length);
  return noReverse ? chunks : chunks.reverse();
};

export default Selection;
